import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import {
  ArrowRight,
  CornerDownLeft,
  FlaskConical,
  History,
  LogOut,
  Monitor,
  Moon,
  Plus,
  Radio,
  Search,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { DESTINATIONS } from '@/app/navigation';
import { usePaletteSearch } from '@/app/palette-search';
import { useRecent } from '@/app/recent';
import { ui, useUi } from '@/app/ui-store';
import { useContextSwitch } from '@/app/use-context-switch';
import type { Ability } from '@/auth/abilities';
import { useSession } from '@/auth/session-context';
import { Kbd } from '@/components/ui/Kbd';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { setThemePreference } from '@/lib/theme';

const CREATE_ACTIONS: { label: string; to: string; ability: Ability; keywords: string[] }[] = [
  { label: 'New customer', to: '/customers?new=1', ability: 'customers:write', keywords: ['create', 'add'] },
  { label: 'New quote', to: '/quotes?new=1', ability: 'quotes:write', keywords: ['create', 'proposal'] },
  { label: 'New contract', to: '/contracts?new=1', ability: 'contracts:write', keywords: ['create'] },
  { label: 'New invoice', to: '/invoices?new=1', ability: 'invoices:write', keywords: ['create', 'bill'] },
  {
    label: 'Record a payment',
    to: '/payments?record=1',
    ability: 'payments:write',
    keywords: ['cheque', 'eft', 'wire'],
  },
  { label: 'New product', to: '/catalog/products?new=1', ability: 'catalog:write', keywords: ['create'] },
  {
    label: 'New price',
    to: '/catalog/prices?new=1',
    ability: 'catalog:write',
    keywords: ['create', 'pricing'],
  },
  {
    label: 'New API key',
    to: '/developers/api-keys?new=1',
    ability: 'api_keys:manage',
    keywords: ['secret'],
  },
  {
    label: 'Invite a team member',
    to: '/settings/team?new=1',
    ability: 'team:manage',
    keywords: ['add', 'member'],
  },
];

function Item({
  icon: Icon,
  children,
  hint,
  onSelect,
  value,
  keywords,
}: {
  icon: LucideIcon;
  children: ReactNode;
  hint?: ReactNode;
  onSelect: () => void;
  value: string;
  keywords?: string[];
}) {
  return (
    <Command.Item value={value} keywords={keywords ?? []} onSelect={onSelect}>
      <Icon aria-hidden className="size-[15px] shrink-0 text-fg-subtle" strokeWidth={1.75} />
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint && <span className="shrink-0 text-xs text-fg-faint">{hint}</span>}
      <CornerDownLeft aria-hidden className="cmdk-enter size-3 shrink-0 text-fg-subtle" />
    </Command.Item>
  );
}

function PaletteBody({ close }: { close: () => void }) {
  const navigate = useNavigate();
  const { session, environment, logout } = useSession();
  const { switchTo } = useContextSwitch();
  const recent = useRecent();
  const [search, setSearch] = useState('');
  const results = usePaletteSearch(search);
  const abilities = session.abilities;
  const allowed = (ability?: Ability) => !ability || abilities.includes(ability);

  const go = (to: string) => {
    close();
    void navigate(to);
  };

  return (
    <Command
      label="Command palette"
      loop
      className={cn(
        'flex flex-col',
        '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:micro-label',
        '[&_[cmdk-item]]:flex [&_[cmdk-item]]:h-8 [&_[cmdk-item]]:cursor-pointer [&_[cmdk-item]]:items-center [&_[cmdk-item]]:gap-2.5 [&_[cmdk-item]]:rounded-md [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:text-base [&_[cmdk-item]]:text-fg-muted',
        '[&_.cmdk-enter]:opacity-0 [&_[cmdk-item][data-selected=true]]:bg-hover [&_[cmdk-item][data-selected=true]]:text-fg [&_[cmdk-item][data-selected=true]_.cmdk-enter]:opacity-100',
      )}
    >
      <div className="flex items-center gap-2 border-b border-border px-3.5">
        <Search aria-hidden className="size-4 shrink-0 text-fg-subtle" />
        <Command.Input
          value={search}
          onValueChange={setSearch}
          autoFocus
          placeholder="Search customers, invoices, or jump to a page…"
          className="h-12 flex-1 bg-transparent text-md text-fg outline-none placeholder:text-fg-faint"
        />
        {results.isFetching && <Spinner className="size-3.5 text-fg-subtle" />}
        <Kbd>Esc</Kbd>
      </div>

      <Command.List className="max-h-[min(420px,60dvh)] overflow-y-auto overscroll-contain p-1.5">
        <Command.Empty className="px-3 py-8 text-center text-base text-fg-subtle">
          No results for “{search}”.
        </Command.Empty>

        {results.groups.map((group) => (
          <Command.Group key={group.heading} heading={group.heading}>
            {group.items.map((item) => (
              <Item
                key={item.path}
                icon={group.icon}
                value={`${group.heading} ${item.title} ${item.subtitle ?? ''} ${item.path}`}
                keywords={[search]}
                hint={item.subtitle}
                onSelect={() => go(item.path)}
              >
                {item.title}
              </Item>
            ))}
          </Command.Group>
        ))}

        {search === '' && recent.length > 0 && (
          <Command.Group heading="Recent">
            {recent.map((item) => (
              <Item
                key={item.path}
                icon={History}
                value={`recent ${item.title} ${item.path}`}
                hint={item.kind}
                onSelect={() => go(item.path)}
              >
                {item.title}
                {item.subtitle && <span className="ml-2 text-fg-subtle">{item.subtitle}</span>}
              </Item>
            ))}
          </Command.Group>
        )}

        <Command.Group heading="Go to">
          {DESTINATIONS.filter((item) => allowed(item.ability)).map((item) => (
            <Item
              key={`${item.to}-${item.label}`}
              icon={item.icon}
              value={`go ${item.area ?? ''} ${item.label}`}
              keywords={item.keywords ?? []}
              onSelect={() => go(item.to)}
              hint={item.area ?? <ArrowRight aria-hidden className="size-3" />}
            >
              {item.label}
            </Item>
          ))}
        </Command.Group>

        <Command.Group heading="Create">
          {CREATE_ACTIONS.filter((action) => allowed(action.ability)).map((action) => (
            <Item
              key={action.to}
              icon={Plus}
              value={action.label}
              keywords={action.keywords}
              onSelect={() => go(action.to)}
            >
              {action.label}
            </Item>
          ))}
        </Command.Group>

        <Command.Group heading="Environment">
          <Item
            icon={environment === 'test' ? Radio : FlaskConical}
            value={environment === 'test' ? 'Switch to live data' : 'Switch to test data'}
            keywords={['environment', 'mode', 'test', 'live', 'sandbox']}
            onSelect={() => {
              close();
              void switchTo({ environment: environment === 'test' ? 'live' : 'test' });
            }}
          >
            {environment === 'test' ? 'Switch to live data' : 'Switch to test data'}
          </Item>
        </Command.Group>

        <Command.Group heading="Preferences">
          <Item
            icon={Sun}
            value="Theme: light"
            keywords={['appearance']}
            onSelect={() => {
              setThemePreference('light');
              close();
            }}
          >
            Use light theme
          </Item>
          <Item
            icon={Moon}
            value="Theme: dark"
            keywords={['appearance']}
            onSelect={() => {
              setThemePreference('dark');
              close();
            }}
          >
            Use dark theme
          </Item>
          <Item
            icon={Monitor}
            value="Theme: system"
            keywords={['appearance', 'auto']}
            onSelect={() => {
              setThemePreference('system');
              close();
            }}
          >
            Match system theme
          </Item>
          <Item
            icon={LogOut}
            value="Sign out"
            keywords={['logout', 'log out']}
            onSelect={() => {
              close();
              void logout();
            }}
          >
            Sign out
          </Item>
        </Command.Group>
      </Command.List>

      <div className="flex items-center gap-3 border-t border-border bg-subtle px-3.5 py-2 text-xs text-fg-subtle">
        <span className="flex items-center gap-1">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> navigate
        </span>
        <span className="flex items-center gap-1">
          <Kbd>↵</Kbd> open
        </span>
        <span className="ml-auto">
          {session.tenant.name} · <span className="font-medium uppercase">{environment}</span>
        </span>
      </div>
    </Command>
  );
}

/** ⌘K command palette at elevation 4. */
export function CommandPalette() {
  const { paletteOpen } = useUi();
  const close = () => ui.setPaletteOpen(false);
  return (
    <DialogPrimitive.Root open={paletteOpen} onOpenChange={ui.setPaletteOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-fade-in bg-backdrop backdrop-blur-[3px] data-[state=closed]:animate-fade-out" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed top-[14vh] left-1/2 z-50 w-[calc(100vw-2rem)] max-w-[620px] -translate-x-1/2 overflow-hidden',
            'rounded-xl border glass-modal shadow-float outline-none',
            'animate-dialog-in data-[state=closed]:animate-dialog-out',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <PaletteBody close={close} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
